import React, { FC } from 'react';
import styled from 'astroturf/react';

import { Body } from 'js/design_system/Body';
import Avatar from './shared/Avatar';

const PLACEHOLDER_PHOTO = '//c.academia-assets.com/images/s65_no_pic.png';

const Container = styled.div<{ small?: boolean }>`
  --avatar-size: 40px;
  display: grid;
  column-gap: 8px;
  grid-template-columns: var(--avatar-size) 1fr;

  & a,
  & div {
    grid-row: 1 / 3;
    width: 100%;
  }

  &.small {
    --avatar-size: 24px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Name = styled(Body)<{ truncateText?: boolean }>`
  @import 'stylesheets/design_system/_breakpoint.scss';
  @import 'stylesheets/design_system/_util.scss';
  @include text-ellipsis;

  &.truncateText {
    @include text-ellipsis;
    width: 168px;
  }

  @include for-tablet-up {
    &.truncateText {
      width: 248px;
    }
  }
`;

const Affiliation = styled(Body)<{ truncateText?: boolean }>`
  @import 'stylesheets/design_system/_breakpoint.scss';
  @import 'stylesheets/design_system/_util.scss';
  @include text-ellipsis;

  &.truncateText {
    @import 'stylesheets/design_system/_util.scss';
    @include text-ellipsis;
    width: 168px;
  }

  @include for-tablet-up {
    &.truncateText {
      width: 248px;
    }
  }

  color: $ds-gray-dark;
`;

export type User = {
  affiliation?: string;
  displayName: string;
  photo?: string;
  /**
   * Determines whether the avatar should render with
   * a wrapping `a` tag or a wrapping `div`.
   * This is useful if you want to use this component inside
   * of a link, since `a` tags cannot contain other `a` tags.
   */
  profileUrl?: string;
};

export type Props = User & {
  /**
   * Allow external stlying via astroturf's `styled` function.
   */
  className?: string;
  size?: 'regular' | 'small';
  truncateText?: boolean;
};

const UserCard: FC<Props> = ({
  affiliation,
  className,
  displayName,
  photo = PLACEHOLDER_PHOTO,
  profileUrl,
  size = 'regular',
  truncateText = false,
}) => (
  <Container className={className} small={size === 'small'}>
    <Avatar
      fullName={displayName}
      photoUrl={photo}
      profileUrl={profileUrl}
      small={size === 'small'}
    />
    <UserInfo>
      <Name
        size={size === 'small' ? 'sm' : 'md'}
        bold
        truncateText={truncateText}
      >
        {displayName}
      </Name>
      <Affiliation
        size={size === 'small' ? 'xs' : 'sm'}
        truncateText={truncateText}
      >
        {affiliation}
      </Affiliation>
    </UserInfo>
  </Container>
);

export default UserCard;
